import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Label } from './ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MergeUsersDialog
 * - If `primaryUser` is provided → row-triggered mode: that user is the merge TARGET (kept),
 *   and SA picks one or more duplicates from a multi-select to merge INTO them.
 * - Otherwise → page-header mode: shows auto-detected duplicate groups + manual 1-to-1 merge.
 * Calls POST /api/admin/users-merge → all references re-pointed, sources deactivated.
 */
const MergeUsersDialog = ({ open, onOpenChange, allUsers, onMerged, primaryUser }) => {
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [manualTarget, setManualTarget] = useState('');
  const [manualSource, setManualSource] = useState('');
  const [merging, setMerging] = useState(false);
  // Row-triggered mode state
  const [selectedSources, setSelectedSources] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!open) return;
    setSelectedSources([]);
    setSearchTerm('');
    // Only fetch duplicates in page-header mode
    if (!primaryUser) {
      setLoading(true);
      axios.get(`${API_URL}/api/admin/users-duplicates`, { withCredentials: true })
        .then(r => setDuplicates(r.data || []))
        .catch(() => setDuplicates([]))
        .finally(() => setLoading(false));
    }
  }, [open, primaryUser]);

  const doMerge = async (target_id, source_ids, label) => {
    if (!target_id || !source_ids || source_ids.length === 0) {
      toast.error('Please select both target and source users');
      return;
    }
    if (!window.confirm(`Merge ${source_ids.length} user(s) into ${label}? This cannot be undone (sources will be deactivated).`)) return;
    setMerging(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/users-merge`,
        { target_id, source_ids },
        { withCredentials: true });
      const s = res.data?.summary || {};
      toast.success(`Merged ✓  ${s.placements_updated || 0} placements · ${s.ledger_updated || 0} ledger · ${s.sources_deactivated || 0} deactivated`);
      onMerged && onMerged();
      if (!primaryUser) {
        const ref = await axios.get(`${API_URL}/api/admin/users-duplicates`, { withCredentials: true });
        setDuplicates(ref.data || []);
      }
      setManualTarget(''); setManualSource(''); setSelectedSources([]);
      if (primaryUser) onOpenChange(false);
    } catch (e) {
      toast.error('Merge failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setMerging(false);
    }
  };

  // -------------- Row-triggered (primary user) mode --------------
  if (primaryUser) {
    const candidates = (allUsers || [])
      .filter(u => u.is_active !== false && u.id !== primaryUser.id)
      .filter(u => !searchTerm || (
        (u.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (u.email || '').toLowerCase().includes(searchTerm.toLowerCase())
      ));
    const toggle = (id) => {
      setSelectedSources(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="merge-into-primary-dialog">
          <DialogHeader>
            <DialogTitle>Merge users into <span className="text-blue-700">{primaryUser.name}</span></DialogTitle>
            <DialogDescription>
              <strong>{primaryUser.name}</strong> ({primaryUser.email}) will be kept as the primary user.
              Select one or more duplicate user accounts to merge INTO this primary user. All placements, ledger entries,
              team memberships, and references will be re-pointed to the primary. Selected duplicates become inactive.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <input
              type="text"
              placeholder="Search users by name or email..."
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="merge-into-primary-search"
            />
            <p className="text-xs text-slate-500">{selectedSources.length} selected · {candidates.length} match</p>
            <div className="border border-slate-200 rounded-md max-h-72 overflow-y-auto divide-y divide-slate-100">
              {candidates.length === 0 && (
                <p className="p-4 text-center text-sm text-slate-400">No users match.</p>
              )}
              {candidates.map(u => (
                <label
                  key={u.id}
                  className="flex items-center gap-2 p-2 hover:bg-slate-50 cursor-pointer text-sm"
                  data-testid={`merge-into-primary-row-${u.id}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedSources.includes(u.id)}
                    onChange={() => toggle(u.id)}
                  />
                  <div className="flex-1">
                    <div className="font-medium text-slate-800">{u.name}</div>
                    <div className="text-xs text-slate-500">{u.email} · {(u.role || '').replace(/_/g, ' ')}{u.team ? ` · ${u.team}` : ''}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              onClick={() => doMerge(primaryUser.id, selectedSources, primaryUser.name)}
              disabled={selectedSources.length === 0 || merging}
              data-testid="merge-into-primary-confirm"
              className="bg-amber-600 hover:bg-amber-700"
            >
              {merging ? 'Merging…' : `Merge ${selectedSources.length} user${selectedSources.length === 1 ? '' : 's'} into ${primaryUser.name}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // -------------- Page-header mode (auto-detect + manual) --------------
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="merge-users-dialog">
        <DialogHeader>
          <DialogTitle>Merge Duplicate Users</DialogTitle>
          <DialogDescription>
            Combine multiple user records into one. All placements, ledger entries, team memberships,
            and references are re-pointed to the kept user. Merged sources become inactive (soft-delete preserves audit history).
          </DialogDescription>
        </DialogHeader>

        {/* Auto-detected duplicates */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">Auto-detected duplicates (by name)</h3>
            <span className="text-xs text-slate-500">{loading ? 'Loading…' : `${duplicates.length} groups`}</span>
          </div>
          {duplicates.length === 0 && !loading && (
            <p className="text-xs text-emerald-700 bg-emerald-50 rounded p-3" data-testid="no-duplicates">No name duplicates detected — looks clean.</p>
          )}
          {duplicates.map((g, idx) => (
            <DuplicateGroup key={idx} group={g} onMerge={doMerge} merging={merging} />
          ))}
        </div>

        {/* Manual merge */}
        <div className="border-t border-slate-200 pt-4 space-y-3">
          <h3 className="text-sm font-semibold text-slate-700">Manual merge</h3>
          <p className="text-xs text-slate-500">Pick the user to keep (target) and the duplicate(s) to merge into them.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Keep this user (target)</Label>
              <Select value={manualTarget} onValueChange={setManualTarget}>
                <SelectTrigger data-testid="merge-manual-target">
                  <SelectValue placeholder="Pick the user to keep" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {(allUsers || []).filter(u => u.is_active !== false).map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name} — {u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Merge this user into target</Label>
              <Select value={manualSource} onValueChange={setManualSource}>
                <SelectTrigger data-testid="merge-manual-source">
                  <SelectValue placeholder="Pick the duplicate" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {(allUsers || []).filter(u => u.is_active !== false && u.id !== manualTarget).map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name} — {u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button
            onClick={() => {
              const target = (allUsers || []).find(u => u.id === manualTarget);
              doMerge(manualTarget, [manualSource], target?.name || 'target');
            }}
            disabled={!manualTarget || !manualSource || merging}
            data-testid="merge-manual-btn"
            className="w-full"
          >
            {merging ? 'Merging…' : 'Merge Manually'}
          </Button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DuplicateGroup = ({ group, onMerge, merging }) => {
  const [targetId, setTargetId] = useState(group.users[0]?.id || '');
  const sourceIds = group.users.filter(u => u.id !== targetId).map(u => u.id);
  const target = group.users.find(u => u.id === targetId);
  return (
    <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-3 space-y-2" data-testid={`dup-group-${group.normalized}`}>
      <p className="text-xs font-semibold text-amber-800">{group.count} copies of "{group.users[0]?.name}"</p>
      <div className="space-y-1">
        {group.users.map(u => (
          <label key={u.id} className="flex items-center gap-2 text-xs cursor-pointer">
            <input
              type="radio"
              name={`keep-${group.normalized}`}
              value={u.id}
              checked={targetId === u.id}
              onChange={() => setTargetId(u.id)}
              data-testid={`dup-keep-${u.id}`}
            />
            <span className={targetId === u.id ? 'font-semibold text-emerald-700' : 'text-slate-700'}>
              {targetId === u.id ? '✓ Keep ' : ''}{u.name} · {u.email} · {u.role}{u.team ? ` · ${u.team}` : ''}
            </span>
          </label>
        ))}
      </div>
      <Button
        size="sm"
        onClick={() => onMerge(targetId, sourceIds, target?.name)}
        disabled={merging}
        data-testid={`dup-merge-${group.normalized}`}
      >
        {merging ? 'Merging…' : `Merge ${sourceIds.length} into ${target?.name}`}
      </Button>
    </div>
  );
};

export default MergeUsersDialog;
